import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IShift } from '../../../../../core/interfaces/shift';
import {
  IProductionDetailByMachine,
  IStatisticsProductionData,
  ISummaryProductionData,
  ITableShiftLoops,
} from 'src/app/modules/dashboard/interfaces/sp/productionbymachine';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { AuthenticationService } from 'projects/authentication/src/app/services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-production-modal',
  templateUrl: './production-modal.component.html',
  styleUrls: ['./production-modal.component.scss'],
})
export class ProductionModalComponent implements OnInit, OnDestroy {
  @Input()
  public title: string;
  @Input()
  public date: string;
  @Input()
  public shift: IShift;
  @Input()
  public data: IProductionDetailByMachine;
  @Input()
  public machineObjCode: string;

  public shiftTitle: string;
  public summaryData: ISummaryProductionData;
  public shiftLoopsData: ITableShiftLoops[];
  public statisticsProductionData: IStatisticsProductionData = {
    productionByOperatorPercentage: [],
    productionByMachinePercentage: [],
    productionByStatus: [],
    productionByEndZone: [],
  };
  public translateTab: string;

  public activeTab = 'summary';

  public logoutSub: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private languageService: LanguageService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private authService: AuthenticationService,
  ) {}

  /**
   * On init component.
   */
  public ngOnInit(): void {
    this.translateTabModal(this.machineObjCode);
    if (this.shift.shiftId !== -1) {
      this.shiftTitle = this.languageService.message('shift', this.shift.name);
    } else {
      this.shiftTitle = this.languageService.message('day_detail');
      this.shiftTitle = this.shiftTitle.charAt(0).toUpperCase() + this.shiftTitle.slice(1);
    }
    this.buildSummaryData();
    this.buildShiftloopsData();
    this.builStatisticsData();
    // 👮🏽 subscribe to the token expired event to close the modal
    this.logoutSub = this.authService.tokenExpired$.subscribe(() => {
      this.activeModal.dismiss('logout');
    });
  }

  ngOnDestroy() {
    // Unsubscribe to avoid memory leaks
    if (this.logoutSub) {
      this.logoutSub.unsubscribe();
    }
  }

  private translateTabModal(machineType: string): void {
    switch (machineType) {
      case 'LHD':
        this.translateTab = this.languageService.message('shelves-shift');
        break;
      case 'JMB':
        this.translateTab = this.languageService.message('drilling-loop');
        break;
      case 'TRU':
        this.translateTab = this.languageService.message('loops-shift');
        break;
      default:
        break;
    }
  }

  /**
   * Builds summary data.
   */
  private buildSummaryData(): void {
    this.summaryData = {
      firstProduction: this.data.firstProduction,
      lastProduction: this.data.lastProduction,
      productionBySectorShift: this.data.productionBySectorShift,
    };
  }

  /**
   * Builds shift loops data.
   */
  private buildShiftloopsData(): void {
    const totalTranslation = this.languageService.message('total');

    if (this.data && this.data.productionByOperatorShift) {
      let shiftLoopsQtyTotal = 0;
      let productionQtyMeters = 0;
      let durationAtSeconds = 0;
      this.shiftLoopsData = this.data.productionByOperatorShift.map((production) => {
        shiftLoopsQtyTotal += production.productionQty;
        durationAtSeconds += production.durationAtSeconds;
        productionQtyMeters += production.productionQtyMeters;
        if (this.machineObjCode === 'JMB') {
          return {
            operator: production.operatorName,
            shiftname: this.languageService.message('shift', production.shiftName),
            street: production.startZoneName,
            durationAtSeconds: `${(production.durationAtSeconds / 60).toFixed(0)} mins`,
            drillingsmts: `${production.productionQtyMeters}`,
          };
        }
        return {
          operator: production.operatorName,
          shiftname: production && production.shiftName ? this.languageService.message('shift', production.shiftName) : '',
          street: production.streetName,
          extractionpoint: production.startZoneName,
          drainingpoint: production.endZoneName,
          loops: `${production.productionQty}`,
        };
      });

      if (this.machineObjCode !== 'JMB') {
        this.shiftLoopsData = [
          ...this.shiftLoopsData,
          {
            operator: totalTranslation,
            shiftname: ' ',
            street: ' ',
            extractionpoint: ' ',
            drainingpoint: ' ',
            loops: `${shiftLoopsQtyTotal}`,
          },
        ];
      } else {
        this.shiftLoopsData = [
          ...this.shiftLoopsData,
          {
            machineName: totalTranslation,
            shiftname: ' ',
            street: ' ',
            durationAtSeconds: `${(durationAtSeconds / 60).toFixed(0)} mins`,
            drillingsmts: `${productionQtyMeters.toFixed(2)}`,
          },
        ];
      }
    }

    if (this.data && this.data.productionByMachineShift) {
      let shiftLoopsQtyTotal = 0;
      let productionQtyMeters = 0;
      let durationAtSeconds = 0;
      this.shiftLoopsData = this.data.productionByMachineShift.map((production) => {
        shiftLoopsQtyTotal += production.productionQty;
        productionQtyMeters += production.productionQtyMeters;
        durationAtSeconds += production.durationAtSeconds;
        if (this.machineObjCode === 'JMB') {
          return {
            machineName: production.machineName,
            shiftname: this.languageService.message('shift', production.shiftName),
            street: production.startZoneName,
            durationAtSeconds: `${(production.durationAtSeconds / 60).toFixed(0)} mins`,
            drillingsmts: `${production.productionQtyMeters}`,
          };
        }
        return {
          machineName: production.machineName,
          shiftname: this.languageService.message('shift', production.shiftName),
          street: production.streetName,
          extractionpoint: production.startZoneName,
          drainingpoint: production.endZoneName,
          loops: `${production.productionQty}`,
        };
      });

      if (this.machineObjCode !== 'JMB') {
        this.shiftLoopsData = [
          ...this.shiftLoopsData,
          {
            machineName: totalTranslation,
            shiftname: ' ',
            street: ' ',
            extractionpoint: ' ',
            drainingpoint: ' ',
            loops: `${shiftLoopsQtyTotal}`,
          },
        ];
      } else {
        this.shiftLoopsData = [
          ...this.shiftLoopsData,
          {
            machineName: totalTranslation,
            shiftname: ' ',
            street: ' ',
            durationAtSeconds: `${(durationAtSeconds / 60).toFixed(0)} mins`,
            drillingsmts: `${productionQtyMeters.toFixed(2)}`,
          },
        ];
      }
    }
  }

  /**
   * Builds statistics data.
   */
  private builStatisticsData(): void {
    if (this.data && this.data.productionByOperatorPercentage) {
      this.statisticsProductionData.productionByOperatorPercentage = this.data.productionByOperatorPercentage.map((production) => {
        if (this.machineObjCode === 'JMB') {
          return {
            machineName: production.operatorName,
            shiftname: this.languageService.message('shift', production.shiftName),
            drillings: `${production.productionQty}`,
            drillingmts: `${production.productionQtyMeters}`,
            percentage: production && production.percentage ? `${production.percentage}%` : '0%',
          };
        }
        return {
          operator: production.operatorName,
          shiftname: production && production.shiftName ? this.languageService.message('shift', production.shiftName) : '',
          loops: `${production.productionQty}`,
          percentage: production && production.percentage ? `${production.percentage}%` : '0%',
        };
      });
      const customEvent = {
        modal_widget: 'production-by-operator',
      };
      this.googleAnalyticsService.sendCustomEvent('modal', customEvent);
    }

    if (this.data && this.data.productionByMachinePercentage) {
      this.statisticsProductionData.productionByMachinePercentage = this.data.productionByMachinePercentage.map((production) => {
        if (this.machineObjCode === 'JMB') {
          return {
            machineName: production.machineName,
            shiftname: this.languageService.message('shift', production.shiftName),
            drillings: `${production.productionQty}`,
            drillingmts: `${production.productionQtyMeters}`,
            percentage: production && production.percentage ? `${production.percentage}%` : '0%',
          };
        }
        return {
          machineName: production.machineName,
          shiftname: this.languageService.message('shift', production.shiftName),
          loops: `${production.productionQty}`,
          percentage: production && production.percentage ? `${production.percentage}%` : '0%',
        };
      });
      const customEvent = {
        modal_widget: 'production-by-machine',
      };
      this.googleAnalyticsService.sendCustomEvent('modal', customEvent);
    }

    if (this.data && this.data.productionByStatus) {
      this.statisticsProductionData.productionByStatus = this.data.productionByStatus.map((production) => {
        return {
          status: production.statusName,
          shiftname: production && production.shiftName ? this.languageService.message('shift', production.shiftName) : '',
          time: production.durationStatusText,
          percentage: production && production.percentage ? `${production.percentage}%` : '0%',
        };
      });
    }

    if (this.data && this.data.productionByEndZone) {
      this.statisticsProductionData.productionByEndZone = this.data.productionByEndZone.map((production) => {
        if (this.machineObjCode !== 'JMB') {
          return {
            drainingpoint: production.zoneName,
            shiftname: production && production.shiftName ? this.languageService.message('shift', production.shiftName) : '',
            shelves: `${production.productionQty}`,
            percentage: production && production.percentage ? `${production.percentage}%` : '0%',
          };
        }
      });
    }
  }

  public changeTab($event: any) {
    let customEvent = {};
    const productionName = this.machineObjCode === 'JMB' ? 'production-by-operator' : 'production-by-machine';
    if ($event.index === 0) {
      customEvent = {
        tab_modal: `${productionName}-summary`,
      };
    }
    if ($event.index === 1) {
      if (this.machineObjCode === 'JMB') {
        customEvent = {
          tab_modal: `${productionName}-drillings`,
        };
      } else {
        customEvent = {
          tab_modal: `${productionName}-loops`,
        };
      }
    }
    if ($event.index === 2) {
      customEvent = {
        tab_modal: `${productionName}-statistics`,
      };
    }
    this.googleAnalyticsService.sendCustomEvent('modal', customEvent);
  }
}
